import { useEffect, useState } from "react";
import { Button } from "./Button";
import "./Paginator.css";

export interface Paginator {
  /** Cursor for the page currently requested — pass straight through as a
   * list query's `params.cursor`. `undefined` means "page 1". */
  cursor: string | undefined;
  /** True once the operator has paged forward at least once this visit —
   * drives the "Previous" button's disabled state. */
  canGoPrevious: boolean;
  /** Step forward using the just-loaded page's `Page.nextCursor` — a no-op
   * if there isn't one (nothing more to page into). */
  goNext: (nextCursor: string | null | undefined) => void;
  /** Step back to the page before the current one. */
  goPrevious: () => void;
}

/**
 * Cursor-stack pagination (charter P-6: every list paginates, and a cursor
 * — never an offset — keeps paging stable while the fleet changes
 * underneath). Lifted out of AuditPage, the first list view in this app to
 * page through more than one screen, so every other list shares the same
 * "cursors visited so far" bookkeeping instead of re-deriving it per view
 * (or, as most views did until now, silently truncating to page 1 — FE-H2).
 *
 * `resetKey` clears back to page 1 whenever it changes (e.g. the active
 * fleet) so stale pagination state never survives a context switch.
 */
export function usePaginator(resetKey?: unknown): Paginator {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [history, setHistory] = useState<string[]>([]);

  useEffect(() => {
    setCursor(undefined);
    setHistory([]);
  }, [resetKey]);

  function goNext(nextCursor: string | null | undefined) {
    if (!nextCursor) return;
    setHistory((h) => [...h, cursor ?? ""]);
    setCursor(nextCursor);
  }

  function goPrevious() {
    setHistory((h) => {
      if (h.length === 0) return h;
      const copy = [...h];
      const previous = copy.pop();
      setCursor(previous || undefined);
      return copy;
    });
  }

  return { cursor, canGoPrevious: history.length > 0, goNext, goPrevious };
}

export interface PaginatorControlsProps {
  /** Rows on the current page — drives the "showing first N" hint. */
  itemCount: number;
  /** The just-loaded page's `Page.nextCursor`. Non-null means the fleet has
   * more rows than this page shows — surfaced explicitly rather than
   * silently hidden (P-6). */
  nextCursor: string | null | undefined;
  paginator: Paginator;
  /** Plural noun for the hint copy, e.g. "containers" (default "rows"). */
  noun?: string;
}

/**
 * Previous/Next controls plus a "showing first N — more are available"
 * hint. Renders nothing once a list's whole result set already fits on one
 * page (no next cursor, no prior page to go back to) — a short list looks
 * exactly as it did before pagination existed.
 */
export function PaginatorControls({ itemCount, nextCursor, paginator, noun = "rows" }: PaginatorControlsProps) {
  const hasNext = Boolean(nextCursor);
  if (!hasNext && !paginator.canGoPrevious) return null;

  return (
    <div className="paginator">
      <span className="paginator__hint">
        {hasNext
          ? `Showing first ${itemCount.toLocaleString()} ${noun} — more are available.`
          : `Showing ${itemCount.toLocaleString()} ${noun}.`}
      </span>
      <div className="paginator__controls">
        <Button variant="secondary" onClick={paginator.goPrevious} disabled={!paginator.canGoPrevious}>
          Previous
        </Button>
        <Button variant="secondary" onClick={() => paginator.goNext(nextCursor)} disabled={!hasNext}>
          Next
        </Button>
      </div>
    </div>
  );
}
